import { Button, Input } from "../../components/ui";
import BreadCrumbs from "../../components/ui/BreadCrumbs";
import Checkbox from "../../components/ui/Checkbox";
import useManageSalaryTemplate from "../../hooks/salaryTemplates/useManageSalaryTemplate";
import AddComponentRowModal from "../../components/salaryTemplates/AddComponentRowModal";
import { useParams } from "react-router-dom";
import { TrashIcon } from "@heroicons/react/solid";
import { roundToTwoDecimalPlaces } from "../../utility/utils";

const breadcrumbs = [
  {
    label: "Salary Templates",
    link: "/salary-templates",
  },
  {
    label: "Manage Salary Template",
    link: "",
  },
];

const ManageSalaryTemplate = () => {
  const { id } = useParams();

  const {
    templateName,
    control,
    watch,
    handleSubmit,
    addRows,
    showAddRowModal,
    toggleAddRowModal,
    handleCTCChange,
    othersMonthlyValue,
    othersAnnualValue,
    handleSave,
    removeRow,
    isSaving,
    statutoryComponentsList,
    isFetchingStatutory,
    // Calculation functions
    calculateMonthlyOnAnnualChange,
    calculateAnnualOnMonthlyChange,
  } = useManageSalaryTemplate({ id: id });

  const earningsArr = watch("earnings") || [];

  const callHandleSave = (data) => {
    const statutoryArr = Object.keys(data.statutory);

    const finalStatutoryArr = statutoryArr.map((key) => {
      const statutoryComponent = statutoryComponentsList.find(
        (sc) => sc.name === key
      );

      return {
        ...statutoryComponent,
        selected: data.statutory[key] || false,
      };
    });

    handleSave({
      template_id: id,
      earnings: data.earnings,
      annual_ctc: 900000,
      monthly_ctc: roundToTwoDecimalPlaces(900000 / 12),
      // annual_ctc: data.annual_ctc,
      // monthly_ctc: roundToTwoDecimalPlaces(parseFloat(data.annual_ctc) / 12),
      statutory: finalStatutoryArr,
      annual_others: othersAnnualValue,
      monthly_others: othersMonthlyValue,
    });
  };

  return (
    <div className="px-5">
      <BreadCrumbs crumbs={breadcrumbs} />

      {showAddRowModal && (
        <AddComponentRowModal
          isOpen={showAddRowModal}
          toggleModal={toggleAddRowModal}
          addRows={addRows}
          addedRows={earningsArr}
        />
      )}

      <h1 className="text-2xl font-bold mt-5">
        Manage salary template for "{templateName}"
      </h1>

      <div className="flex justify-end mb-10 mt-5">
        <Input
          type="number"
          control={control}
          name="annual_ctc"
          label="Annual CTC"
          blockUntilOnChangeIsTrue={true}
          isPositiveNumber={true}
          className="w-64"
          onChangeFn={(e) => handleCTCChange(e)}
          rules={{ required: "Annual CTC is required" }}
        />
      </div>

      <div className="flex items-center justify-between my-5">
        <p className="font-semibold">Earnings:</p>

        <Button onClick={toggleAddRowModal}>Add row</Button>
      </div>

      <div className="border p-5 border-gray-300 rounded-md grid grid-cols-7 items-center">
        <p className="font-medium col-span-2">Salary components</p>
        <p className="font-medium col-span-2">Monthly amount</p>
        <p className="font-medium col-span-2">Annual amount</p>
        <p></p>

        {/* {earningsArr?.length === 0 && (
          <p className="text-gray-400 pt-16 pb-10 col-span-7 text-center">
            No earning components added
          </p>
        )} */}

        {earningsArr?.map((el, idx) => {
          return (
            <>
              <div className="pt-4 col-span-2">{el.name}</div>
              <div className="pt-4 col-span-2">
                <Input
                  type="number"
                  isPositiveNumber={true}
                  control={control}
                  className="border p-1 rounded mr-5"
                  parentClassName="mr-5"
                  blockUntilOnChangeIsTrue={true}
                  onChangeFn={(e) => calculateAnnualOnMonthlyChange(e, idx)}
                  disabled={!el.is_monthly_editable}
                  name={`earnings[${idx}].monthly_value`}
                />
              </div>
              <div className="pt-4 col-span-2">
                <Input
                  type="number"
                  isPositiveNumber={true}
                  control={control}
                  className="border p-1 rounded"
                  blockUntilOnChangeIsTrue={true}
                  onChangeFn={(e) => calculateMonthlyOnAnnualChange(e, idx)}
                  disabled={!el.is_yearly_editable}
                  name={`earnings[${idx}].annual_value`}
                />
              </div>
              <div className="pt-4 grid place-items-center h-full">
                <Button variant={"ghost"} size={"icon"}>
                  <TrashIcon
                    className="w-6 h-6 text-red-500"
                    onClick={() => removeRow(idx)}
                  />
                </Button>
              </div>
            </>
          );
        })}

        <p className="pt-4 col-span-2">Others</p>
        <p className="pt-4 col-span-2">&#8377; {othersMonthlyValue}</p>
        <p className="pt-4 col-span-2">&#8377; {othersAnnualValue}</p>
        <p className="pt-4"></p>
      </div>

      <p className="font-semibold mt-5 mb-4">Statutory components:</p>

      {isFetchingStatutory && (
        <p className="text-gray-500 py-10">Loading components...</p>
      )}

      {statutoryComponentsList.map((sc, idx) => {
        return (
          <div key={idx} className="mb-4">
            <div className="flex items-center mb-1.5">
              <Checkbox
                label={sc.name}
                control={control}
                name={`statutory.${sc.name}`}
              />

              {sc.manage_components && (
                <Button variant="outline" className={"ml-3 !py-1"}>
                  Manage components
                </Button>
              )}
            </div>
            <p className="ml-5 text-sm text-gray-500">{sc.note}</p>
          </div>
        );
      })}

      <div className="flex justify-end">
        <Button onClick={handleSubmit(callHandleSave)} className="mt-5">
          Save Template
        </Button>
      </div>
      {isSaving && <p className="text-right w-full py-4">Saving...</p>}
    </div>
  );
};

export default ManageSalaryTemplate;
